import { db } from '../firebase';
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc,
  query, where, orderBy, Timestamp, deleteDoc, writeBatch
} from 'firebase/firestore';

/**
 * Subject list for task creation
 */
export const COACH_SUBJECTS = {
  TYT: ['Türkçe', 'Matematik', 'Fizik', 'Kimya', 'Biyoloji', 'Tarih', 'Coğrafya', 'Felsefe', 'Din Kültürü'],
  AYT: ['Matematik', 'Fizik', 'Kimya', 'Biyoloji', 'Türk Dili ve Edebiyatı', 'Tarih', 'Coğrafya', 'Felsefe'],
  Genel: ['Deneme Çözümü', 'Paragraf Çalışması', 'Soru Bankası', 'Tekrar', 'Diğer'],
};

export const COACH_SUBJECTS_LGS = {
  LGS: ['Türkçe', 'Matematik', 'Fen Bilimleri', 'T.C. İnkılap Tarihi', 'Din Kültürü', 'Yabancı Dil'],
  Genel: ['Deneme Çözümü', 'Paragraf Çalışması', 'Soru Bankası', 'Tekrar', 'Diğer'],
};

/** Color map for subjects */
export const SUBJECT_COLORS = {
  'Türkçe': '#f59e0b',
  'Matematik': '#6366f1',
  'Fizik': '#ef4444',
  'Kimya': '#10b981',
  'Biyoloji': '#22d3ee',
  'Tarih': '#f97316',
  'Coğrafya': '#84cc16',
  'Felsefe': '#a855f7',
  'Din Kültürü': '#ec4899',
  'Türk Dili ve Edebiyatı': '#f59e0b',
  'Fen Bilimleri': '#10b981',
  'T.C. İnkılap Tarihi': '#f97316',
  'Yabancı Dil': '#3b82f6',
  'Deneme Çözümü': '#14b8a6',
  'Paragraf Çalışması': '#eab308',
  'Soru Bankası': '#8b5cf6',
  'Tekrar': '#64748b',
  'Diğer': '#94a3b8',
};

/**
 * Set a user's role to "coach" (admin only)
 */
export const setUserRole = async (userId, role) => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, { role });
};

/**
 * Assign a student to a coach (admin only)
 */
export const assignStudentToCoach = async (studentId, coachId) => {
  const studentRef = doc(db, 'users', studentId);
  await updateDoc(studentRef, { coachId });
};

/**
 * Remove a student from their coach
 */
export const removeStudentFromCoach = async (studentId) => {
  const studentRef = doc(db, 'users', studentId);
  await updateDoc(studentRef, { coachId: null });
};

/**
 * Get all students assigned to a coach
 */
export const getStudentsByCoach = async (coachId) => {
  const q = query(collection(db, 'users'), where('coachId', '==', coachId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Get all coaches
 */
export const getAllCoaches = async () => {
  const q = query(collection(db, 'users'), where('role', '==', 'coach'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Get coach info for a student
 */
export const getCoachForStudent = async (studentId) => {
  const studentRef = doc(db, 'users', studentId);
  const studentDoc = await getDoc(studentRef);
  if (!studentDoc.exists()) return null;
  const coachId = studentDoc.data().coachId;
  if (!coachId) return null;
  const coachRef = doc(db, 'users', coachId);
  const coachDoc = await getDoc(coachRef);
  if (!coachDoc.exists()) return null;
  return { id: coachDoc.id, ...coachDoc.data() };
};

// ── Task CRUD ──

/**
 * Create a task for a student
 */
export const createTask = async (taskData) => {
  const ref = await addDoc(collection(db, 'coachTasks'), {
    ...taskData,
    status: 'pending',
    completedAt: null,
    createdAt: Timestamp.now(),
  });
  return ref.id;
};

/**
 * Get tasks for a student on a specific week
 */
export const getTasksForStudentWeek = async (studentId, weekStartDate) => {
  const q = query(
    collection(db, 'coachTasks'),
    where('studentId', '==', studentId),
    where('weekStartDate', '==', weekStartDate),
    orderBy('date'),
    orderBy('order')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Get tasks for a student on a specific date
 */
export const getTasksForStudentDate = async (studentId, date) => {
  const q = query(
    collection(db, 'coachTasks'),
    where('studentId', '==', studentId),
    where('date', '==', date),
    orderBy('order')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Get all tasks for a student (no date filter)
 */
export const getAllTasksForStudent = async (studentId) => {
  const q = query(
    collection(db, 'coachTasks'),
    where('studentId', '==', studentId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Update task status (student marking as done/skipped)
 */
export const updateTaskStatus = async (taskId, status) => {
  const taskRef = doc(db, 'coachTasks', taskId);
  const update = { status };
  if (status === 'completed') {
    update.completedAt = Timestamp.now();
  } else {
    update.completedAt = null;
  }
  await updateDoc(taskRef, update);
};

/**
 * Update a task (coach editing)
 */
export const updateTask = async (taskId, data) => {
  const taskRef = doc(db, 'coachTasks', taskId);
  await updateDoc(taskRef, data);
};

/**
 * Delete a task
 */
export const deleteTask = async (taskId) => {
  await deleteDoc(doc(db, 'coachTasks', taskId));
};

/**
 * Delete all tasks for a student on a specific date
 */
export const clearDayTasks = async (studentId, date) => {
  const q = query(
    collection(db, 'coachTasks'),
    where('studentId', '==', studentId),
    where('date', '==', date)
  );
  const snapshot = await getDocs(q);
  const batch = writeBatch(db);
  snapshot.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
};

/**
 * Delete all tasks for a student for a specific week
 */
export const clearWeekTasks = async (studentId, weekStartDate) => {
  const q = query(
    collection(db, 'coachTasks'),
    where('studentId', '==', studentId),
    where('weekStartDate', '==', weekStartDate)
  );
  const snapshot = await getDocs(q);
  const batch = writeBatch(db);
  snapshot.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
};

// ── Notes CRUD ──

/**
 * Save/update a coach note for a student
 */
export const saveCoachNote = async (coachId, studentId, content) => {
  const q = query(
    collection(db, 'coachNotes'),
    where('coachId', '==', coachId),
    where('studentId', '==', studentId)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    await addDoc(collection(db, 'coachNotes'), {
      coachId,
      studentId,
      content,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  } else {
    const noteRef = snapshot.docs[0].ref;
    await updateDoc(noteRef, { content, updatedAt: Timestamp.now() });
  }
};

/**
 * Get coach note for a student
 */
export const getCoachNote = async (coachId, studentId) => {
  const q = query(
    collection(db, 'coachNotes'),
    where('coachId', '==', coachId),
    where('studentId', '==', studentId)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
};

// ── Day Notes CRUD ──

/**
 * Get all day notes for a student
 */
export const getAllDayNotesForStudent = async (studentId) => {
  const q = query(
    collection(db, 'dayNotes'),
    where('studentId', '==', studentId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Save/update a note for a specific day and student
 */
export const saveDayNote = async (studentId, date, note) => {
  const q = query(
    collection(db, 'dayNotes'),
    where('studentId', '==', studentId),
    where('date', '==', date)
  );
  const snapshot = await getDocs(q);

  if (!note || note.trim() === '') {
    // Delete if empty
    if (!snapshot.empty) {
      await deleteDoc(snapshot.docs[0].ref);
    }
    return;
  }

  if (snapshot.empty) {
    await addDoc(collection(db, 'dayNotes'), {
      studentId,
      date,
      note,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  } else {
    await updateDoc(snapshot.docs[0].ref, { note, updatedAt: Timestamp.now() });
  }
};

/**
 * Get all day notes for a student in a specific week
 */
export const getDayNotesForWeek = async (studentId, weekStartDate) => {
  // To avoid complex date range queries, we just fetch all notes for the student 
  // or fetch by exact dates if we know them. Since we only want a week, we can just fetch all 
  // for the student and filter in memory, or add weekStartDate to the document.
  // Adding weekStartDate is better.
  const q = query(
    collection(db, 'dayNotes'),
    where('studentId', '==', studentId),
    where('weekStartDate', '==', weekStartDate)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Save/update a note for a specific day and student (with weekStartDate)
 */
export const saveDayNoteWithWeek = async (studentId, date, weekStartDate, note) => {
  const q = query(
    collection(db, 'dayNotes'),
    where('studentId', '==', studentId),
    where('date', '==', date)
  );
  const snapshot = await getDocs(q);

  if (!note || note.trim() === '') {
    if (!snapshot.empty) {
      await deleteDoc(snapshot.docs[0].ref);
    }
    return;
  }

  if (snapshot.empty) {
    await addDoc(collection(db, 'dayNotes'), {
      studentId,
      date,
      weekStartDate,
      note,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  } else {
    await updateDoc(snapshot.docs[0].ref, { note, weekStartDate, updatedAt: Timestamp.now() });
  }
};

// ── Helpers ──

/**
 * Returns a gradient color based on the exam type for avatars
 */
export const getExamColor = (examType) => {
  if (!examType) return 'linear-gradient(135deg, #cbd5e1, #94a3b8)'; // Slate
  const upper = examType.toUpperCase();
  if (upper.includes('LGS')) return 'linear-gradient(135deg, #34d399, #10b981)'; // Emerald
  if (upper.includes('YDT')) return 'linear-gradient(135deg, #fcd34d, #f59e0b)'; // Amber
  if (upper.includes('YKS') || upper.includes('TYT') || upper.includes('AYT')) return 'linear-gradient(135deg, #a5b4fc, #6366f1)'; // Indigo
  return 'linear-gradient(135deg, #a5b4fc, #818cf8)'; // Default
};

/**
 * Get the Monday of the week containing the given date
 */
export const getWeekStartDate = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  d.setDate(diff);
  return d.toISOString().split('T')[0];
};

/**
 * Get all 7 days of the week from a start date
 */
export const getWeekDays = (weekStartDate) => {
  const days = [];
  const start = new Date(weekStartDate);
  const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    days.push({
      date: d.toISOString().split('T')[0],
      dayName: dayNames[i],
      dayNumber: d.getDate(),
    });
  }
  return days;
};

/**
 * Copies all tasks from a source week to a target week for a specific student
 */
export const copyWeekTasks = async (studentId, coachId, fromWeekStart, toWeekStart) => {
  const fromTasks = await getTasksForStudentWeek(studentId, fromWeekStart);
  if (fromTasks.length === 0) return 0;
  
  const batch = writeBatch(db);
  const diffTime = Math.abs(new Date(toWeekStart) - new Date(fromWeekStart));
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  const isForward = new Date(toWeekStart) > new Date(fromWeekStart);
  
  fromTasks.forEach(task => {
    const taskDate = new Date(task.date);
    if (isForward) {
      taskDate.setDate(taskDate.getDate() + diffDays);
    } else {
      taskDate.setDate(taskDate.getDate() - diffDays);
    }
    const newDateStr = taskDate.toISOString().split('T')[0];
    
    // Create reference under coachTasks
    const newTaskRef = doc(collection(db, 'coachTasks'));
    batch.set(newTaskRef, {
      coachId,
      studentId,
      date: newDateStr,
      weekStartDate: toWeekStart,
      title: task.title,
      subject: task.subject,
      category: task.category,
      description: task.description || null,
      startTime: task.startTime || null,
      duration: task.duration || null,
      order: task.order || 0,
      status: 'pending',
      completedAt: null,
      createdAt: Timestamp.now()
    });
  });
  
  await batch.commit();
  return fromTasks.length;
};

